import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Headphones, Image as ImageIcon, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import socketService from '../services/socketService';
import { chatService } from '../services/chatService';
import API_CONFIG from '../config/api';
import { useTranslation } from '../i18n/LocaleContext';

const ChatPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [reactions, setReactions] = useState({});
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const response = await chatService.getChatHistory(50, 0);
      if (response.success) {
        setMessages(response.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          imageUrl: msg.image_url,
          sender: msg.sender_role === 'admin' ? 'admin' : 'user',
          timestamp: new Date(msg.created_at),
          username: msg.sender_username
        })));
        setHasMore(response.hasMore);
        setOffset(50);
      }
    } catch (error) {
      console.error('Load chat history error:', error);
    }
  };

  const loadMoreMessages = async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);
      const container = messagesContainerRef.current;
      previousScrollHeightRef.current = container.scrollHeight;

      const response = await chatService.getChatHistory(50, offset);
      if (response.success && response.messages.length > 0) {
        const newMessages = response.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          imageUrl: msg.image_url,
          sender: msg.sender_role === 'admin' ? 'admin' : 'user',
          timestamp: new Date(msg.created_at),
          username: msg.sender_username
        }));

        setMessages(prev => [...newMessages, ...prev]);
        setHasMore(response.hasMore);
        setOffset(prev => prev + 50);

        // Giữ vị trí scroll
        setTimeout(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - previousScrollHeightRef.current;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Load more messages error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop === 0 && hasMore && !loading) {
      loadMoreMessages();
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Load lịch sử chat từ API
    loadChatHistory();

    // Kết nối socket
    const socket = socketService.connect(token);

    const onConnect = () => {
      setConnected(true);
      socketService.markAsRead();
    };

    const onDisconnect = () => {
      setConnected(false);
    };

    // Lắng nghe tin nhắn mới
    const onNewMessage = (message) => {
      setMessages(prev => [...prev, {
        id: message.id,
        text: message.message,
        imageUrl: message.image_url,
        sender: message.sender_role === 'admin' ? 'admin' : 'user',
        timestamp: new Date(message.created_at),
        username: message.sender_username
      }]);
      // 正在客服页时收到客服回复，直接算已读
      if (message.sender_role === 'admin') socketService.markAsRead();
    };

    // Lắng nghe typing
    const onTyping = (data) => {
      if (data.isAdmin) {
        setTyping(true);
        setTimeout(() => setTyping(false), 3000);
      }
    };

    const onStopTyping = () => {
      setTyping(false);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('new-message', onNewMessage);
    socket.on('user-typing', onTyping);
    socket.on('user-stop-typing', onStopTyping);

    // socket 可能由钱包页提前连好：此时 connect 事件早已发生，必须立即置为已连接
    if (socket.connected) {
      onConnect();
    }

    return () => {
      // 只摘掉本页的监听，不销毁共享连接（钱包页的未读红点还在用同一个 socket）
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('new-message', onNewMessage);
      socket.off('user-typing', onTyping);
      socket.off('user-stop-typing', onStopTyping);
    };
  }, []);

  const handleSend = async () => {
    if ((!inputText.trim() && !selectedImage) || !connected || uploadingImage) return;

    const messageText = inputText;
    const imageFile = selectedImage;
    setInputText('');
    setSelectedImage(null);
    setImagePreview(null);
    socketService.stopTyping();

    let imageUrl = null;
    if (imageFile) {
      try {
        setUploadingImage(true);
        const uploadRes = await chatService.uploadImage(imageFile);
        if (uploadRes.success) {
          imageUrl = uploadRes.imageUrl;
        }
      } catch (error) {
        console.error('Upload image error:', error);
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }

    socketService.sendMessage(messageText, null, (response) => {
      if (!response.success) {
        console.error('Send message error:', response.error);
      }
    }, imageUrl);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);

    // Gửi typing indicator
    socketService.startTyping();

    // Clear timeout cũ
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout mới
    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping();
    }, 1000);
  };

  const lastAgentMessage = [...messages].reverse().find((m) => m.sender === 'admin');

  const handleCopy = (message) => {
    if (!message.text) return;
    navigator.clipboard?.writeText(message.text);
    setCopiedId(message.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header — Notion AI style: ghost buttons at the edges, avatar + name centered */}
      <div className="flex-shrink-0 bg-white">
        <div className="max-w-md mx-auto px-4 pt-3 pb-1 grid grid-cols-[40px_1fr_40px] items-start">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t('back')}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center text-ink"
          >
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <div className="flex flex-col items-center">
            <span className="w-12 h-12 rounded-full bg-white border border-black/[0.06] flex items-center justify-center text-ink">
              <Headphones className="w-5 h-5" />
            </span>
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!connected || uploadingImage}
            aria-label={t('attachImage')}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center text-ink disabled:opacity-40"
          >
            {uploadingImage ? (
              <div className="w-4 h-4 border-2 border-ink border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <ImageIcon className="w-[18px] h-[18px]" />
            )}
          </button>
        </div>
      </div>

      {/* Messages — user = gray pill right; support = plain text left, Notion answer style */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto bg-white"
      >
        <div className="max-w-md mx-auto px-5 py-5">
          {loading && (
            <div className="text-center py-2">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-ink"></div>
            </div>
          )}

          {!hasMore && messages.length > 0 && (
            <div className="text-center py-2 text-ink-muted text-xs">
              {t('allHistoryShown')}
            </div>
          )}

          {messages.map((message) => {
            const isUser = message.sender === 'user';
            return (
              <div key={message.id} className={`mb-5 ${isUser ? 'flex justify-end' : ''}`}>
                {message.imageUrl && (
                  <img
                    src={API_CONFIG.getImageUrl(message.imageUrl)}
                    alt="chat"
                    className={`max-w-[70%] rounded-xl mb-1.5 cursor-pointer ${isUser ? 'ml-auto' : ''}`}
                    onClick={() => window.open(API_CONFIG.getImageUrl(message.imageUrl), '_blank')}
                  />
                )}
                {message.text && (isUser ? (
                  <p className="max-w-[80%] bg-canvas text-ink rounded-2xl rounded-br-md px-5 py-2.5 text-[14.5px] leading-6 whitespace-pre-line">
                    {message.text}
                  </p>
                ) : (
                  <>
                    <p className="max-w-[92%] text-[15px] text-ink leading-7 whitespace-pre-line">{message.text}</p>
                    {lastAgentMessage && message.id === lastAgentMessage.id && (
                      <div className="flex items-center gap-1 mt-1 -ml-2">
                        <button
                          type="button"
                          aria-label={t('copyMessage')}
                          onClick={() => handleCopy(message)}
                          className="p-2 rounded-lg text-ink-muted hover:bg-canvas transition-colors"
                        >
                          {copiedId === message.id ? <Check className="w-[18px] h-[18px]" /> : <Copy className="w-[18px] h-[18px]" />}
                        </button>
                        <button
                          type="button"
                          aria-label={t('helpful')}
                          onClick={() => setReactions((p) => ({ ...p, [message.id]: p[message.id] === 'up' ? null : 'up' }))}
                          className={`p-2 rounded-lg transition-colors hover:bg-canvas ${reactions[message.id] === 'up' ? 'text-ink bg-canvas' : 'text-ink-muted'}`}
                        >
                          <ThumbsUp className="w-[18px] h-[18px]" />
                        </button>
                        <button
                          type="button"
                          aria-label={t('notHelpful')}
                          onClick={() => setReactions((p) => ({ ...p, [message.id]: p[message.id] === 'down' ? null : 'down' }))}
                          className={`p-2 rounded-lg transition-colors hover:bg-canvas ${reactions[message.id] === 'down' ? 'text-ink bg-canvas' : 'text-ink-muted'}`}
                        >
                          <ThumbsDown className="w-[18px] h-[18px]" />
                        </button>
                      </div>
                    )}
                  </>
                ))}
              </div>
            );
          })}

          {typing && (
            <div className="mb-4 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input — floating pill above BottomNav */}
      <div className="flex-shrink-0 bg-white pb-20">
        {!connected && (
          <div className="text-ink-muted text-xs text-center py-1">
            {t('connecting')}
          </div>
        )}
        {imagePreview && (
          <div className="max-w-md mx-auto px-4 pt-3">
            <div className="relative inline-block">
              <img src={imagePreview} alt="preview" className="h-20 rounded-xl border border-black/10" />
              <button
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 w-6 h-6 bg-ink text-white rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        <div className="max-w-md mx-auto px-4 pt-2 pb-3">
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageSelect}
            className="hidden"
          />
          <div className="flex items-center bg-canvas rounded-xl pl-5 pr-1.5">
            <input
              type="text"
              value={inputText}
              onChange={handleInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={connected ? t('typeMessage') : t('connecting')}
              disabled={!connected}
              className="flex-1 min-w-0 h-12 bg-transparent text-[14.5px] text-ink placeholder:text-ink-muted border-0 focus:outline-none disabled:opacity-60"
            />
            <button
              onClick={handleSend}
              disabled={!connected || (!inputText.trim() && !selectedImage) || uploadingImage}
              aria-label={t('send')}
              className="w-10 h-10 shrink-0 rounded-full bg-brand-600 text-white flex items-center justify-center transition-colors active:bg-brand-700 disabled:bg-transparent disabled:text-ink-muted disabled:cursor-default"
            >
              <Send className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
